import { useMemo, useState } from 'react';

import { apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { StatusBadge } from '@app/components/StatusBadge';

interface InvoiceRow {
  id: string;
  invoice_number: string;
  customer_email: string;
  customer_company: string;
  status: string;
  total_amount: string;
  period_start: string;
  period_end: string;
  messages_cost: string;
  payment_due_date: string;
  paid_date: string | null;
  created_at: string;
}

const repo = new AdminListRepository<InvoiceRow>(apiClient, 'invoices');

const columns: DataTableColumn<InvoiceRow>[] = [
  { id: 'invoice_number', header: 'Invoice #', accessor: 'invoice_number' },
  { id: 'customer_email', header: 'Customer', accessor: 'customer_email' },
  { id: 'period', header: 'Period', accessor: (r) => `${r.period_start} – ${r.period_end}` },
  { id: 'status', header: 'Status', accessor: (r) => <StatusBadge value={r.status} /> },
  // Statements of prepaid usage: usage was paid from the balance when sent; only a plan fee is due
  { id: 'messages_cost', header: 'Usage (prepaid)', accessor: (r) => `$${r.messages_cost}` },
  { id: 'total_amount', header: 'Due', accessor: (r) => `$${r.total_amount}` },
  { id: 'payment_due_date', header: 'Due', accessor: 'payment_due_date' },
  { id: 'paid_date', header: 'Paid', accessor: (r) => (r.paid_date ? new Date(r.paid_date).toLocaleDateString() : '—') },
];

export function InvoicesPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filters = useMemo(() => {
    const f: Record<string, string> = {};
    if (search) f.search = search;
    if (statusFilter) f.status = statusFilter;
    return f;
  }, [search, statusFilter]);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-semibold">
          Statements
          <span className="ml-2 text-xs font-normal text-slate-500">
            monthly, prepaid usage — only a plan fee is ever due
          </span>
        </h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <input
            type="search"
            placeholder="Search invoice # or customer"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded border px-2 py-1"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border px-2 py-1"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
      </div>
      <DataTable<InvoiceRow>
        columns={columns}
        fetcher={(p) => repo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={25}
        rowKey={(r) => r.id}
        emptyState={<span data-testid="datatable-empty">No invoices yet.</span>}
      />
    </section>
  );
}
